import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormControl,
    FormGroup,
    RadioGroup,
    FormControlLabel,
    Radio,
    makeStyles,
    FormLabel,
} from '@material-ui/core';
import { PROSJEKT_TYPE } from '@klp/formgenerator/interfaces';
import { useGetProjectStepTypesQuery } from '../../../services/rtkApi';
import { Skeleton } from '@klp/layout/Skeleton';

const useStyles = makeStyles((theme) => ({
    formField: {
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    label: {
        marginTop: theme.spacing(1),
        '& div': {
            lineHeight: 1.1,
        },
    },
    extraLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.9em',
    },
}));

export interface StartStepChooseProps {
    projectType: PROSJEKT_TYPE;
    value?: string;
    touched?: boolean;
    error?: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const StartStepChoose: React.FC<StartStepChooseProps> = ({
    projectType,
    value,
    touched,
    error,
    onChange,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { data: projectSteps, isLoading } = useGetProjectStepTypesQuery({
        type: projectType,
        onlyStartingSteps: true,
    });

    const previousProjectStepsRef = useRef<
        Record<string, number> | undefined
    >();

    useEffect(() => {
        if (!projectSteps) return;
        if (
            JSON.stringify(projectSteps) ===
            JSON.stringify(previousProjectStepsRef.current)
        ) {
            return;
        }
        previousProjectStepsRef.current = projectSteps;

        if (Object.keys(projectSteps).length !== 1) return;

        // Auto-select single step
        const singleStepValue = Object.values(projectSteps)[0];
        onChange({
            target: {
                name: 'startStep',
                value: singleStepValue.toString(),
            },
        } as React.ChangeEvent<HTMLInputElement>);
    }, [projectSteps, onChange]);

    const getStepExtraLabel = (stepName: string, type: PROSJEKT_TYPE) => {
        const extraLabel = t(
            `project.opprettProsjekt.radioGroup.${stepName}.${PROSJEKT_TYPE[type]}`,
        );
        if (extraLabel === PROSJEKT_TYPE[type]) {
            // No translation found for this case
            return null;
        }

        return <div className={classes.extraLabel}>({extraLabel})</div>;
    };

    const getStepLabel = (stepName: string) => {
        return (
            <div>
                {t(`project.opprettProsjekt.radioGroup.${stepName}.title`)}
                {getStepExtraLabel(stepName, projectType)}
            </div>
        );
    };

    if (!projectSteps || Object.keys(projectSteps).length <= 1) {
        return null;
    }

    return (
        <Skeleton isLoading={isLoading}>
            <FormControl
                required={true}
                component="fieldset"
                fullWidth={true}
                margin="normal"
                error={touched && !!error}
            >
                <FormGroup>
                    <FormLabel component="legend">
                        {t(`project.opprettProsjekt.selectStep`)}
                    </FormLabel>
                    <RadioGroup
                        row={false}
                        id="startStep"
                        name="startStep"
                        onChange={onChange}
                    >
                        {Object.entries(projectSteps).map(
                            ([stepName, stepValue]) => (
                                <FormControlLabel
                                    key={stepValue}
                                    value={stepValue}
                                    checked={value === stepValue.toString()}
                                    control={<Radio color="primary" />}
                                    label={getStepLabel(stepName)}
                                    className={classes.label}
                                />
                            ),
                        )}
                    </RadioGroup>
                </FormGroup>
            </FormControl>
        </Skeleton>
    );
};
